import {
  AttachingMethod,
  Attachment,
  AttachmentType,
  ChatbotUIAction,
  SelectorClickedItem,
  SelectorType,
  Type,
  UserActionType,
} from '@wix/ambassador-funnel-chatbot-v1-session/types';
import { ScheduledCallbacksListSelectorButton } from '@wix/wix-chatbot-common/selectors';
import { useCallback } from 'react';
import { batch, useDispatch, useSelector, useStore } from 'react-redux';
import { Store } from 'redux';
import { v4 as uuid } from 'uuid';

import { MESSAGES, SELECTOR_TYPE_RPC, SELECTOR_TYPE_TO_ITEM_NAME } from '../../../constants';
import {
  AttachingMethod as AttachingMethodClient,
  Attachment as AttachmentClient,
  AttachmentType as AttachmentTypeClient,
} from '../../../types';
import { isUndefined } from '../../../util/guards';
import { ChatbotSendActionType, logChatbotErrorMessagePresented, SEND_ERROR_TYPE } from '../../bi';
import { logResendButtonClicked } from '../../bi/loggers/logResendButtonClicked';
import { sendUserAction, SendUserActionError } from '../../common/api/sendUserAction';
import {
  createBotTyping,
  createUserMessage,
  IArticle,
  IArticleSelector,
  IBotMessage,
  IButtons,
  ICategory,
  IDomainSelector,
  IImage,
  IIntentSelector,
  IInvoiceSelector,
  IPhone,
  IPlanSelector,
  IScheduledCallbacksListSelector,
  ISiteSelector,
  ISuggestionSelector,
  ITicketSelector,
  ITimeslotSelector,
  IUserAccountsSelector,
  IUserMessage,
  IVideo,
} from '../../common/messageCreators';
import { useHttpClient } from '../../common/providers/httpClient';
import { TRootState } from '../../common/reducers';
import { chatActions } from '../../common/reducers/chat';
import { errorsSlice } from '../../common/reducers/errors';
import { fieldActions } from '../../common/reducers/field';
import { IUserAction, TItem } from '../../common/reducers/types';
import { selectIsLiveChat, selectUndelivered } from '../../common/selectors/chat';
import { selectFieldNodeId, selectIsChange } from '../../common/selectors/field';
import { createHTML } from '../../util/createHTML';

type TUserMessage = Partial<IUserMessage>;

const getChangeId = (store: Store<TRootState>): string | undefined => {
  const state = store.getState();

  if (selectIsChange(state)) {
    return selectFieldNodeId(state);
  }

  return undefined;
};

const getLoadPageUrl = (store: Store<TRootState>): string => {
  const state = store.getState();
  return state.meta.loadPageUrl;
};

const getConversationId = (store: Store<TRootState>): string | undefined => {
  const state = store.getState();
  return state.chat.conversationId;
};
const getNodeId = (store: Store<TRootState>): string | undefined => {
  const state = store.getState();
  return selectFieldNodeId(state);
};

const errorCatcher = (
  e: string,
  store: Store<TRootState>,
  action: UserActionType | IUserAction,
  messageId: string,
  messageText: string | undefined,
) => {
  store.dispatch(chatActions.addUndelivered({ [messageId]: action }));

  const state = store.getState();
  const isRetryAvailable = action.type !== ChatbotUIAction.SEND_MESSAGE;
  const retryData = isRetryAvailable ? { messageId, messageText } : undefined;
  const isClosable = !isRetryAvailable;

  let errorType: SEND_ERROR_TYPE;

  switch (e) {
    case SendUserActionError.network:
      store.dispatch(
        errorsSlice.actions.addError({ localKey: 'send_message_network_error', retryData, isClosable }),
      );
      errorType = SEND_ERROR_TYPE.NETWORK_ERROR;
      break;
    case SendUserActionError.timeout:
      store.dispatch(
        errorsSlice.actions.addError({ localKey: 'send_message_timeout_error', retryData, isClosable }),
      );
      errorType = SEND_ERROR_TYPE.TIMEOUT_ERROR;
      break;
    default:
      store.dispatch(
        errorsSlice.actions.addError({ localKey: 'send_message_tech_error', retryData, isClosable }),
      );
      errorType = SEND_ERROR_TYPE.TECH_ERROR;
  }

  logChatbotErrorMessagePresented(state, {
    messageId,
    messageText: messageText || '',
    errorType,
    isTryAgainAvailable: retryData !== undefined,
    buttonName: retryData ? 'try_again' : undefined,
  });
};

export const useUserActionClickButton = () => {
  const httpClient = useHttpClient();
  const store = useStore<TRootState>();
  const isLiveChat = useSelector(selectIsLiveChat);

  type TButtonDetail = {
    _id: string;
    label: string;
    link?: string;
    sublabel?: string;
    position: number;
  };

  return (detail: TButtonDetail) => {
    const id = uuid();
    const changeId = getChangeId(store);
    const conversationId = getConversationId(store);
    const nodeId = getNodeId(store);
    const loadPageUrl = getLoadPageUrl(store);

    const data: TUserMessage = {
      id,
      text: detail.label,
      changeId,
    };

    batch(() => {
      store.dispatch(chatActions.addItem(createUserMessage(data, isLiveChat)));
      store.dispatch(
        fieldActions.sendMessage({
          bi: { sendActionType: ChatbotSendActionType.Button },
        }),
      );
      store.dispatch(chatActions.addItem(createBotTyping({ rm: true })));
    });

    const { _id, ...restDetails } = detail;

    const action: UserActionType = {
      type: ChatbotUIAction.BUTTON_CLICKED,
      buttonClicked: {
        messageId: id,
        changeId,
        id: _id,
        loadPageUrl,
        ...restDetails,
      },
    };

    sendUserAction(action, conversationId, nodeId, httpClient).catch((e) => {
      errorCatcher(e, store, action, id, detail.label);
    });
  };
};

export const useUserActionClickSelector = () => {
  const httpClient = useHttpClient();
  const store = useStore<TRootState>();

  type TSelectorType =
    | MESSAGES.PHONE
    | MESSAGES.ARTICLE_SELECTOR
    | MESSAGES.INVOICE_SELECTOR
    | MESSAGES.DOMAIN_SELECTOR
    | MESSAGES.TICKET_SELECTOR
    | MESSAGES.PLAN_SELECTOR
    | MESSAGES.SUGGESTION_SELECTOR
    | MESSAGES.SITE_SELECTOR
    | MESSAGES.TIMESLOT_SELECTOR
    | MESSAGES.SCHEDULED_CALLBACKS_LIST_SELECTOR
    | MESSAGES.USER_ACCOUNTS_SELECTOR
    | MESSAGES.INTENT_SELECTOR;

  type TSelectorDetail = {
    label: string;
    item?: Record<string, any>;
    position?: number;
    footer?: true;
    lastMessage?: TItem;
    btnType?: ScheduledCallbacksListSelectorButton;
  };

  return useCallback(
    (type: TSelectorType, detail: TSelectorDetail, skip?: boolean) => {
      const id = uuid();
      const changeId = getChangeId(store);
      const conversationId = getConversationId(store);
      const nodeId = getNodeId(store);
      const loadPageUrl = getLoadPageUrl(store);
      const data: TUserMessage = {
        id,
        text: detail.label,
        changeId,
      };

      if (!skip) {
        batch(() => {
          store.dispatch(chatActions.addItem(createUserMessage(data, false)));
          store.dispatch(
            fieldActions.sendMessage({
              bi: { sendActionType: ChatbotSendActionType.SelectorButton },
            }),
          );
          store.dispatch(chatActions.addItem(createBotTyping({ rm: true })));
        });
      }

      const item: SelectorClickedItem = {};

      if (detail.item) {
        // eslint-disable-next-line default-case
        switch (type) {
          case MESSAGES.PHONE:
            item[SELECTOR_TYPE_TO_ITEM_NAME[type]] = { ...detail.item };
            break;
          case MESSAGES.ARTICLE_SELECTOR:
          case MESSAGES.INVOICE_SELECTOR:
          case MESSAGES.DOMAIN_SELECTOR:
          case MESSAGES.TICKET_SELECTOR:
          case MESSAGES.PLAN_SELECTOR:
          case MESSAGES.SUGGESTION_SELECTOR:
          case MESSAGES.SITE_SELECTOR:
          case MESSAGES.TIMESLOT_SELECTOR:
          case MESSAGES.SCHEDULED_CALLBACKS_LIST_SELECTOR:
          case MESSAGES.USER_ACCOUNTS_SELECTOR:
            const { _id, ...restItem } = detail.item;
            item[SELECTOR_TYPE_TO_ITEM_NAME[type]] = { id: _id, ...restItem };
            break;
        }
      }

      const lastMessageNodeId = (
        detail.lastMessage as
          | IArticle
          | IBotMessage
          | IButtons
          | IPhone
          | IVideo
          | IImage
          | ICategory
          | ISiteSelector
          | IDomainSelector
          | IPlanSelector
          | IArticleSelector
          | IInvoiceSelector
          | ISuggestionSelector
          | ITicketSelector
          | ITimeslotSelector
          | IScheduledCallbacksListSelector
          | IUserAccountsSelector
          | IIntentSelector
      )?.nodeId;

      const action: UserActionType = {
        skip,
        type: ChatbotUIAction.SELECTOR_CLICKED,
        selectorClicked: {
          messageId: id,
          changeId,
          type: SelectorType[SELECTOR_TYPE_RPC[type]],
          label: detail.label,
          position: detail.position,
          footer: detail.footer,
          ...(lastMessageNodeId
            ? {
                lastMessage: {
                  nodeId: lastMessageNodeId || '',
                },
              }
            : null),
          loadPageUrl,
          ...(Object.keys(item).length ? { item } : null),
        },
      };

      sendUserAction(action, conversationId, nodeId, httpClient).catch((e) => {
        if (!skip) {
          errorCatcher(e, store, action, id, detail.label);
        }
      });
    },
    [httpClient, store],
  );
};

export const useUserActionClickCategory = () => {
  const httpClient = useHttpClient();
  const store = useStore<TRootState>();

  return (type: MESSAGES.CATEGORY, detail) => {
    const id = uuid();
    const changeId = getChangeId(store);
    const conversationId = getConversationId(store);
    const nodeId = getNodeId(store);
    const loadPageUrl = getLoadPageUrl(store);
    const data: TUserMessage = {
      id,
      text: detail.label,
      changeId,
    };

    batch(() => {
      store.dispatch(chatActions.addItem(createUserMessage(data, false)));
      store.dispatch(
        fieldActions.sendMessage({
          bi: { sendActionType: ChatbotSendActionType.CategoryButton },
        }),
      );
      store.dispatch(chatActions.addItem(createBotTyping({ rm: true })));
    });

    const { _id, ...restItem } = detail.item;

    const action: UserActionType = {
      type: ChatbotUIAction.CATEGORY_CLICKED,
      categoryClicked: {
        messageId: id,
        changeId,
        type: 'CATEGORY' as Type,
        label: detail.label,
        position: detail.position,
        loadPageUrl,
        item: {
          id: _id,
          ...restItem,
        },
      },
    };

    sendUserAction(action, conversationId, nodeId, httpClient).catch((e) => {
      errorCatcher(e, store, action, id, detail.label);
    });
  };
};

export const useUserActionSendMessage = () => {
  const httpClient = useHttpClient();
  const store = useStore<TRootState>();

  type TUserMessageDetail = {
    value: string;
    action_type: string;
    attachments: AttachmentClient[];
    isLiveChat: boolean;
  };

  return ({ value, action_type, attachments, isLiveChat }: TUserMessageDetail) => {
    const id = uuid();
    const changeId = getChangeId(store);
    const conversationId = getConversationId(store);
    const nodeId = getNodeId(store);
    const loadPageUrl = getLoadPageUrl(store);
    const useHtmlContent = isLiveChat || attachments.length;
    const content = useHtmlContent ? createHTML(value, attachments) : value;

    const userMessage: TUserMessage = {
      id,
      changeId,
      ...(useHtmlContent ? { html: content } : { text: content }),
    };

    const attachmentMapper = (attachment: AttachmentClient): Attachment => {
      const { name, url, width, height, size, attachingMethod } = attachment;
      const attachmentMethodMapper: Record<AttachingMethodClient, AttachingMethod> = {
        [AttachingMethodClient.Button]: AttachingMethod.BUTTON,
        [AttachingMethodClient.Drop]: AttachingMethod.DROP,
        [AttachingMethodClient.Paste]: AttachingMethod.PASTE,
      };
      const attachmentBase = {
        name,
        url,
        width,
        height,
        size,
        attachingMethod: attachmentMethodMapper[attachingMethod],
      };

      if (attachment.type === AttachmentTypeClient.Video) {
        const { iconUrl, duration, format } = attachment;
        return {
          ...attachmentBase,
          iconUrl,
          duration,
          format,
          type: AttachmentType.VIDEO,
        };
      } else {
        return {
          ...attachmentBase,
          type: AttachmentType.IMAGE,
        };
      }
    };
    const sendActionType =
      action_type === 'ctrl+enter' || action_type === 'enter'
        ? ChatbotSendActionType.Enter
        : ChatbotSendActionType.SubmitButton;

    batch(() => {
      store.dispatch(chatActions.addItem(createUserMessage(userMessage, isLiveChat)));
      store.dispatch(
        fieldActions.sendMessage({
          bi: { sendActionType },
        }),
      );
      !isLiveChat && store.dispatch(chatActions.addItem(createBotTyping({ rm: true })));
    });

    const action: UserActionType = {
      type: ChatbotUIAction.SEND_MESSAGE,
      sendMessage: {
        messageId: id,
        attachments: attachments.map(attachmentMapper),
        changeId,
        value: content,
        actionType: action_type,
        loadPageUrl,
      },
    };

    sendUserAction(action, conversationId, nodeId, httpClient).catch((e: SendUserActionError) => {
      errorCatcher(e, store, action, id, userMessage.text);
    });
  };
};

export const useResend = (id: string | undefined, messageText: string | undefined) => {
  const httpClient = useHttpClient();
  const dispatch = useDispatch();
  const store = useStore<TRootState>();
  const action = useSelector(selectUndelivered(id));
  const hasAction = !isUndefined(action);

  const resend = (): void => {
    if (id) {
      const state = store.getState();
      logResendButtonClicked(state, { messageId: id, messageText: messageText || '' });
      if (hasAction) {
        const conversationId = getConversationId(store);
        const nodeId = getNodeId(store);
        dispatch(chatActions.removeUndelivered(id));
        dispatch(chatActions.addItem(createBotTyping({ rm: true })));

        sendUserAction(action as UserActionType, conversationId, nodeId, httpClient).catch((e) => {
          errorCatcher(e, store, action, id, messageText);
        });
      }
    }
  };

  return <const>[hasAction, resend];
};
